import React from 'react';
import { Box, Typography, Stepper, Step, StepLabel, StepConnector } from '@mui/material';
import { styled } from '@mui/material/styles';
import { CheckCircle, Restaurant, Kitchen, Person } from '@mui/icons-material';

interface TrackingHistoryItem {
  status: string;
  timestamp: string;
}

interface TrackingHistoryTimelineProps {
  trackingHistory: TrackingHistoryItem[];
  currentStatus: string;
}

const QontoConnector = styled(StepConnector)(() => ({
  '&.Mui-active': {
    '& .MuiStepConnector-line': {
      borderColor: '#4caf50',
      borderWidth: 2,
    },
  },
  '&.Mui-completed': {
    '& .MuiStepConnector-line': {
      borderColor: '#4caf50',
      borderWidth: 2,
    },
  },
  '& .MuiStepConnector-line': {
    borderColor: '#e0e0e0',
    borderTopWidth: 2,
  },
}));


const getStatusIcon = (status: string) => {
  switch (status.toLowerCase()) {
    case 'order placed':
      return <Restaurant sx={{ color: 'white', fontSize: 14 }} />;
    case 'merchant accepted':
      return <CheckCircle sx={{ color: 'white', fontSize: 14 }} />;
    case 'order preparing':
      return <Kitchen sx={{ color: 'white', fontSize: 14 }} />;
    case 'ready for pickup':
      return <CheckCircle sx={{ color: 'white', fontSize: 14 }} />;
    case 'order delivered':
      return <Person sx={{ color: 'white', fontSize: 14 }} />;
    default:
      return <CheckCircle sx={{ color: 'white', fontSize: 14 }} />;
  }
};

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'order placed':
      return '#4caf50';
    case 'merchant accepted':
      return '#4caf50';
    case 'order preparing':
      return '#4caf50';
    case 'ready for pickup':
      return '#4caf50';
    case 'order delivered':
      return '#4caf50';
    default:
      return '#4caf50';
  }
};

const TrackingHistoryTimeline: React.FC<TrackingHistoryTimelineProps> = ({
  trackingHistory,
  currentStatus
}) => {
  // Define the complete order flow
  const orderFlow = [
    'Order Placed',
    'Merchant Accepted',
    'Order Preparing',
    'Ready for Pickup',
    'Order Delivered'
  ];

  // Check if order is cancelled
  const isOrderCancelled = () => {
    return currentStatus.toLowerCase().includes('cancelled') || 
           currentStatus.toLowerCase().includes('cancel') ||
           trackingHistory.some(item => 
             item.status.toLowerCase().includes('cancelled') || 
             item.status.toLowerCase().includes('cancel')
           );
  };

  // Find the current step index based on tracking history
  const getCurrentStepIndex = () => {
    // If order is cancelled, only show Order Placed as completed
    if (isOrderCancelled()) {
      return 0; // Only Order Placed is completed
    }

    // Find the highest completed step based on tracking history
    let maxCompletedIndex = -1;
    
    if (trackingHistory && trackingHistory.length > 0) {
      trackingHistory.forEach(item => {
        const stepIndex = orderFlow.findIndex(step => 
          step.toLowerCase() === item.status.toLowerCase()
        );
        if (stepIndex > maxCompletedIndex) {
          maxCompletedIndex = stepIndex;
        }
      });
    }
    
    // If no tracking history, fall back to currentStatus
    if (maxCompletedIndex === -1) {
      const statusIndex = orderFlow.findIndex(step => 
        step.toLowerCase() === currentStatus.toLowerCase()
      );
      return statusIndex >= 0 ? statusIndex : 0;
    }
    
    return maxCompletedIndex;
  };

  const currentStepIndex = getCurrentStepIndex();
  const orderIsCancelled = isOrderCancelled();

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      {orderIsCancelled ? (
        // Show cancelled timeline using Stepper to match normal timeline UI
        <Stepper
          activeStep={trackingHistory ? trackingHistory.length - 1 : 1}
          connector={<QontoConnector />}
          orientation="horizontal"
          sx={{
            '& .MuiStepLabel-root': {
              padding: 0,
              flex: 1,
            },
            '& .MuiStep-root': {
              flex: 1,
            },
          }}
        >
          {trackingHistory && trackingHistory.length > 0 ? (
            trackingHistory.map((item, index) => {
              const isCancelled = item.status.toLowerCase().includes('cancelled') || item.status.toLowerCase().includes('cancel');
              
              return (
                <Step key={index}>
                  <StepLabel
                    StepIconComponent={() => (
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          backgroundColor: isCancelled ? '#f44336' : '#4caf50',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid white',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                        }}
                      >
                        {isCancelled ? (
                          <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✕</Typography>
                        ) : (
                          getStatusIcon(item.status)
                        )}
                      </Box>
                    )}
                    sx={{
                      '& .MuiStepLabel-label': {
                        fontSize: '11px',
                        fontWeight: 500,
                        color: isCancelled ? '#f44336' : '#333',
                        mt: 0.5,
                        textAlign: 'center',
                      },
                    }}
                  >
                    {item.status}
                  </StepLabel>
                </Step>
              );
            })
          ) : (
            // Fallback if no tracking history
            <>
              <Step>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        backgroundColor: '#4caf50',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid white',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                      }}
                    >
                      {getStatusIcon('Order Placed')}
                    </Box>
                  )}
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontSize: '11px',
                      fontWeight: 500,
                      color: '#333',
                      mt: 0.5,
                      textAlign: 'center',
                    },
                  }}
                >
                  Order Placed
                </StepLabel>
              </Step>
              <Step>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        backgroundColor: '#f44336',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid white',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                      }}
                    >
                      <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✕</Typography>
                    </Box>
                  )}
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontSize: '11px',
                      fontWeight: 500,
                      color: '#f44336',
                      mt: 0.5,
                      textAlign: 'center',
                    },
                  }}
                >
                  Order Cancelled
                </StepLabel>
              </Step>
            </>
          )}
        </Stepper>
      ) : (
        // Show normal timeline
        <Stepper
          activeStep={currentStepIndex}
          connector={<QontoConnector />}
          orientation="horizontal"
          sx={{
            '& .MuiStepLabel-root': {
              padding: 0,
              flex: 1,
            },
            '& .MuiStep-root': {
              flex: 1,
            },
          }}
        >
          {orderFlow.map((step, index) => (
            <Step key={step}>
              <StepLabel
                StepIconComponent={() => (
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      backgroundColor: index <= currentStepIndex ? getStatusColor(step) : '#e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                    }}
                  >
                    {getStatusIcon(step)}
                  </Box>
                )}
                sx={{
                  '& .MuiStepLabel-label': {
                    fontSize: '11px',
                    fontWeight: 500,
                    color: index <= currentStepIndex ? '#333' : '#999',
                    mt: 0.5,
                    textAlign: 'center',
                  },
                }}
              >
                {step}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      )}
    </Box>
  );
};

export default TrackingHistoryTimeline;
